import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import OutwardEntryForm from "../components/OutwardEntryForm";
import { fetchOutwards, deleteOutward } from "../api/inwardOutwardApi";
import { getCsrfToken } from "../../../public/api/authApi";
import RegisterListTable, {
  getInwardOutwardCellText,
  getInwardOutwardColumnDefs,
  loadInwardOutwardSavedVisibility
} from "../components/RegisterListTable";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../global/directory";
import { INWARD_OUTWARD_PAGE_SIZE } from "../utils/inwardOutwardFormConstants";
import { useInwardOutwardRole } from "../utils/useInwardOutwardRole";
import { OUTWARDS_REGISTER_SUBMODULE_SLUG } from "../utils/inwardOutwardPermissionSlugs";

// Reusing userBuilder styles for consistency
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/inwardOutwardDirectory.css";

const OutwardsRegisterPage = () => {
  const perms = useInwardOutwardRole(OUTWARDS_REGISTER_SUBMODULE_SLUG);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [lookupsVersion, setLookupsVersion] = useState(0);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);
  const [directoryFilters, setDirectoryFilters] = useState({
    search: "",
    column: "",
    value: ""
  });
  const [directoryPage, setDirectoryPage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [summaryStats, setSummaryStats] = useState({
    totalOutwards: 0,
    todayOutwards: 0,
  });
  const columnDefs = getInwardOutwardColumnDefs("outward");
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadInwardOutwardSavedVisibility("outward");
    const initial = {};
    for (const c of columnDefs) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    return initial;
  });

  const normalizeLocalDate = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const fetchRecords = useCallback(async () => {
    setLoadingRecords(true);
    setErrorMsg("");
    try {
      const data = await fetchOutwards();
      const outwardRows = data?.data || [];
      setRecords(outwardRows);
      const today = normalizeLocalDate(new Date());
      setSummaryStats({
        totalOutwards: outwardRows.length,
        todayOutwards: outwardRows.filter((row) => normalizeLocalDate(row.date_sent) === today).length,
      });
    } catch (err) {
      setErrorMsg(err.message || "Failed to fetch records.");
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, lookupsVersion]);

  const handleFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setEditingRecord(null);
    fetchRecords();
  };

  const handleRequestEdit = (row) => {
    setEditingRecord(row);
    requestAnimationFrame(() => {
      document
        .getElementById("ms-create")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDelete = (id) => {
    const rec = records.find((m) => m.id === id);
    setRecordToDelete(rec);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete || !perms.canDelete) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteOutward(recordToDelete.id, csrfToken);
      setSuccessMsg("Record deleted successfully.");
      setShowConfirmModal(false);
      setRecordToDelete(null);
      fetchRecords();
    } catch (err) {
      setErrorMsg(err.message || "Failed to delete record.");
    } finally {
      setIsConfirming(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    ...(perms.canView ? [{ key: "stats", label: "Outward metrics", targetId: "ms-stats" }] : []),
    ...(perms.canCreate || (editingRecord && perms.canEdit)
      ? [{ key: "create", label: "Add Outward Entry", targetId: "ms-create" }]
      : []),
    ...(perms.canView ? [{ key: "list", label: "Outwards Register", targetId: "ms-list" }] : []),
  ];

  const statsItems = [
    { key: "total-outwards", label: "Total Outwards", value: summaryStats.totalOutwards, tone: "mint" },
    { key: "today-outwards", label: "Today Outwards", value: summaryStats.todayOutwards, tone: "coral" },
  ];

  const valueOptions = React.useMemo(() => {
    if (!directoryFilters.column) return [];
    const set = new Set();
    records.forEach((row) => {
      const text = String(getInwardOutwardCellText(directoryFilters.column, row) || "—").trim();
      if (text) set.add(text);
    });
    return Array.from(set).sort().map((v) => ({ value: v, label: v }));
  }, [records, directoryFilters.column]);

  const filteredRecords = React.useMemo(() => {
    const q = directoryFilters.search.trim().toLowerCase();
    const col = directoryFilters.column;
    const val = directoryFilters.value.trim().toLowerCase();
    return records.filter((row) => {
      const matchesSearch = !q || columnDefs.some((c) =>
        String(getInwardOutwardCellText(c.id, row) || "").toLowerCase().includes(q)
      );
      if (!matchesSearch) return false;
      if (!col || !val) return true;
      return String(getInwardOutwardCellText(col, row) || "").toLowerCase() === val;
    });
  }, [records, directoryFilters, columnDefs]);

  React.useEffect(() => {
    setDirectoryPage(1);
  }, [directoryFilters]);

  const directoryTotal = filteredRecords.length;
  const directoryTotalPages = Math.max(1, Math.ceil(directoryTotal / INWARD_OUTWARD_PAGE_SIZE));
  const page = Math.min(directoryPage, directoryTotalPages);

  const pagedRecords = React.useMemo(() => {
    const start = (page - 1) * INWARD_OUTWARD_PAGE_SIZE;
    return filteredRecords.slice(start, start + INWARD_OUTWARD_PAGE_SIZE);
  }, [filteredRecords, page]);

  const visibleCols = columnDefs.filter((c) => columnVisibility[c.id]);
  const getExportData = React.useCallback(() => ({
    headers: visibleCols.map((c) => c.label),
    rows: filteredRecords.map((row) => visibleCols.map((c) => getInwardOutwardCellText(c.id, row)))
  }), [filteredRecords, visibleCols]);

  if (perms.permissionsLoading) {
    return (
      <div className="app-screen-guard app-screen-guard--loading">
        <p>Loading access…</p>
      </div>
    );
  }

  if (!perms.canView) {
    return <Navigate to="/user-login" replace />;
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Office Management</p>
          <h2 className="user-builder-hero__title">Outwards Register</h2>
          <p className="user-builder-hero__sub">
            Manage outgoing correspondence and documents. Add new outward entries, edit existing records, and upload supporting documents.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {perms.canView ? (
          <div
            id="ms-stats"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Outward Metrics</h3>
              <p className="user-builder-panel__hint">
                Snapshot of total and today outward entries.
              </p>
            </div>
            <GlobalMetricCards
              items={statsItems}
              loading={loadingRecords}
              skeletonCount={2}
              ariaLabel="Office outward quick statistics"
            />
          </div>
          ) : null}

          {(perms.canCreate || (editingRecord && perms.canEdit)) ? (
          <div
            id="ms-create"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Add Outward Form</h3>
              <p className="user-builder-panel__hint">
                Create a new outward register entry or edit an existing one.
              </p>
            </div>
            <OutwardEntryForm
              lookupsRefreshKey={lookupsVersion}
              onSuccess={handleFormSuccess}
              editingRecord={editingRecord}
              onCancelEdit={() => setEditingRecord(null)}
            />
          </div>
          ) : null}
          
          {perms.canView ? (
          <div
            id="ms-list"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head io-directory-panel-head">
              <h3 className="user-builder-panel__title">Office Table</h3>
              <p className="user-builder-panel__hint">
                View and manage all outward register entries.
              </p>
            </div>
            <GlobalDirectoryShell
              className="gds-shell--compact io-directory-shell"
              filters={(
                <GlobalDirectoryFiltersSection
                  searchLabel="Search"
                  searchPlaceholder="Search outward records"
                  searchValue={directoryFilters.search}
                  onSearchChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, search: v }))
                  }
                  columnLabel="Column"
                  columnValue={directoryFilters.column}
                  onColumnChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, column: v, value: "" }))
                  }
                  valueLabel="Value"
                  valueValue={directoryFilters.value}
                  onValueChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, value: v }))
                  }
                  valueOptions={valueOptions}
                  gridColumnDefs={columnDefs}
                  onClear={() => setDirectoryFilters({ search: "", column: "", value: "" })}
                  secondaryStart={(
                    <>
                      <GlobalDirectoryColumnPicker
                        columnDefs={columnDefs}
                        visibility={columnVisibility}
                        onVisibilityChange={setColumnVisibility}
                        storageKey="girinestham.inwardOutwardList.columnVisibility.outward"
                      />
                    </>
                  )}
                  secondaryEnd={perms.canExport ? (
                    <GlobalDirectoryExportMenu
                      getExportData={getExportData}
                      filenameBase="office_outward_register"
                    />
                  ) : null}
                />
              )}
              grid={(
                <RegisterListTable
                  records={pagedRecords}
                  registerType="outward"
                  loading={loadingRecords}
                  onDelete={perms.canDelete ? handleDelete : null}
                  onRequestEdit={perms.canEdit ? handleRequestEdit : undefined}
                  showExport={false}
                  showToolbar={false}
                  columnVisibilityOverride={columnVisibility}
                />
              )}
              pagination={(
                <GlobalDirectoryPaginationSection
                  page={page}
                  totalPages={directoryTotalPages}
                  total={directoryTotal}
                  pageSize={INWARD_OUTWARD_PAGE_SIZE}
                  onPageChange={setDirectoryPage}
                  label="Outward register pagination"
                />
              )}
            />
          </div>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Dashboard sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title="Delete outward record?"
        footer={(
          <>
            <GlobalButton
              variant="ghost"
              onClick={() => setShowConfirmModal(false)}
              disabled={isConfirming}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isConfirming}
            >
              Delete
            </GlobalButton>
          </>
        )}
      >
        <p>
          Remove outward record{" "}
          <b>{recordToDelete?.outward_no || recordToDelete?.id}</b> from the register?
        </p>
      </GlobalModal>
    </div>
  );
};

export default OutwardsRegisterPage;
